import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { styled } from "@mui/material/styles";
import { find, get, isEqual } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import Dropzone from "react-dropzone";
import { acceptedFileTypes } from "../utils/constants";
import DocumentListRefactor from "./DocumentListRefactor";
import DocumentListViewOnly from "./DocumentListViewOnly";

const DocumentDropContainer = styled("div")({
    padding: 0,
});

const StyledDropzoneContainer = styled("div")({
    borderRadius: "3px",
    border: "2px dashed #dbdbdb",
});

const StyledDropzoneWAttachmentsContainer = styled("div")({
    borderRadius: "3px",
    border: "2px dashed #dbdbdb",
    marginTop: "10px",
});

const StyledDropzoneComponent = styled(Dropzone)({
    width: "100%",
    "& > input": {
        width: "100%",
    },
    textAlign: "center",
    paddingTop: "64px",
    paddingBottom: "64px",
    minHeight: "50vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
});

const StyledDropzoneComponentWAttachments = styled(Dropzone)({
    width: "100%",
    "& > input": {
        width: "100%",
    },
    textAlign: "center",
    minHeight: "10vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
});

const IconStyled = styled(CloudUploadIcon)({
    width: "70px",
    height: "70px",
    color: "#9E9E9E",
});

const StyledHelperTextContainer = styled("div")({
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
});

const StyledDropzoneHelperContent = styled("div")({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "20px",
    justifyContent: "center",
    gap: "1vw",
});

const headers = [
    {
        name: "Name",
        prop: "documentName",
        type: "plaintext",
        extraHeaderStyles: {
            width: "40%",
        },
    },
    {
        name: "Document Type",
        prop: "documentType",
        type: "plaintext",
        extraHeaderStyles: {
            width: "50%",
        },
    },
    {
        name: "",
        prop: "",
        type: "plaintext",
        extraHeaderStyles: {
            width: "10%",
        },
    },
];

const approvalHeaders = [
    {
        name: "Name",
        prop: "documentName",
        type: "plaintext",
        extraHeaderStyles: {
            width: "40%",
        },
    },
    {
        name: "Document Type",
        prop: "documentType",
        type: "plaintext",
        extraHeaderStyles: {
            width: "30%",
        },
    },
    {
        name: "",
        prop: "",
        type: "plaintext",
        extraHeaderStyles: {
            width: "20%",
        },
    },
    {
        name: "Document Type",
        prop: "documentType",
        type: "plaintext",
        extraHeaderStyles: {
            width: "10%",
        },
    },
];

const DocumentDrop = (props) => {
    const {
        addDocuments,
        allDocumentTypes,
        approveDocument,
        columnToSort,
        disableUploads,
        documentsUploading,
        documentTypes,
        documentRequirements,
        downloadDocument,
        downloadView,
        entityKey,
        errors,
        formDisabled,
        handleDocumentSort,
        handleRejectedDocs,
        includeSource,
        loading,
        noHeader,
        parentType,
        removeDocument,
        requiredDocs,
        resetRequiredMissingDocuments,
        role,
        savedDocuments,
        setAdminDocumentTypeListAvailable,
        sortDirection,
        stateCode,
        terminationDocument,
        toggleTerminationDoc,
        type,
        updateDocument, // (key, mediaAssetType)
        usedDocTypes,
        viewDocument,
        viewOnly,
    } = props;

    const [anchorEl, setAnchorEl] = useState(null);
    const [docKey, setDocKey] = useState(null);
    const [adminListReady, setAdminListReady] = useState(false);
    const [missingDocs, setMissingDocs] = useState([]);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);

    const prevProps = useRef({ savedDocuments, requiredDocs });

    useEffect(() => {
        return () => {
            resetRequiredMissingDocuments();
        };
    }, []);

    useEffect(() => {
        adminDocumentTypeListAvailable();
    }, [documentRequirements]);

    useEffect(() => {
        if (
            !isEqual(savedDocuments, prevProps.current.savedDocuments) ||
            !isEqual(requiredDocs, prevProps.current.requiredDocs)
        ) {
            determineMissingRequiredDocuments();
        }
        prevProps.current = { savedDocuments, requiredDocs };
    }, [savedDocuments, requiredDocs]);

    const handleOpenDocumentMenu = (x, anchorElArg) => {
        setAnchorEl(anchorElArg);
        setDocKey(x);
    };

    const toggleDeleteDialog = (open, docKey) => {
        if (open) {
            handleMenuClose();
            setDeleteDialog(true);
            setDocToDelete(docKey);
        } else {
            setDeleteDialog(false);
            setDocToDelete(null);
        }
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const deleteAndMenuClose = () => {
        setAnchorEl(null);
        removeDocument(docToDelete);
        toggleDeleteDialog(false);
    };

    const previewAndMenuClose = (docKey, url) => {
        setAnchorEl(null);
        viewDocument(docKey, url);
    };

    const handleAddDocuments = (accepted, rejected, ownerType) => {
        addDocuments(entityKey, type, accepted, ownerType);
        if (rejected.length > 0) {
            handleRejectedDocs(rejected);
        }
    };

    const handleApproveDocumentAndClose = (docKey) => {
        setAnchorEl(null);
        approveDocument(docKey);
    };

    const adminDocumentTypeListAvailable = () => {
        if (parentType) {
            setAdminDocumentTypeListAvailable();
            setAdminListReady(true);
        }
    };

    const determineMissingRequiredDocuments = () => {
        const docTypeRequiredList = get(props, "requiredDocs", []);

        const fulfilledDocs = docTypeRequiredList
            .map((requiredDocument) => {
                const savedDocumentMatch = find(savedDocuments, (savedDoc) => {
                    return requiredDocument.documentTypeKey === get(savedDoc, "documentType.documentTypeKey");
                });

                return savedDocumentMatch ? get(savedDocumentMatch, "documentType.documentTypeKey") : null;
            })
            .filter((doc) => doc);

        const missingDocsLocal = docTypeRequiredList.filter(
            (document) => !fulfilledDocs.includes(document.documentTypeKey)
        );
        setMissingDocs(missingDocsLocal);
    };

    const renderHelperMessage = (type) => {
        return (
            <React.Fragment>
                {type === "comp" && <p>For best results, the Marketing Flyer Photo should be 1224x440px.</p>}
                {type === "voucher" && (
                    <div style={{ fontSize: "13px", marginTop: "10px", fontStyle: "italic" }}>
                        You will be paid fastest if you attach your Commission Payment or Equivalent
                    </div>
                )}
                {type === "property" && (
                    <div style={{ fontSize: "13px", marginTop: "10px", fontStyle: "italic" }}>
                        Preferred specs for Primary Photos:
                        <ul style={{ marginTop: "2px", listStyleType: "none", paddingLeft: "20px" }}>
                            <li>4:3 Aspect Ratio</li>
                            <li>1440 x 1080 Pixels (Resolution)</li>
                        </ul>
                    </div>
                )}
                {missingDocs.length > 0 && (
                    <div style={{ color: "#E53935", fontSize: "13px", marginTop: "10px", fontStyle: "italic" }}>
                        Missing required documents for submission:
                        <ul style={{ marginTop: "2px", listStyleType: "none", paddingLeft: "20px" }}>
                            {missingDocs.map((doc) => (
                                <li>{doc["name"]}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </React.Fragment>
        );
    };

    const open = Boolean(anchorEl);

    return viewOnly ? (
        <DocumentListViewOnly
            {...props}
            open={open}
            anchorEl={anchorEl}
            docMenuKey={docKey}
            documentTypes={documentTypes}
            handlePreviewDocument={previewAndMenuClose}
            handleDownloadDocument={downloadDocument}
            handleMenuClose={handleMenuClose}
            handleOpenDocumentMenu={handleOpenDocumentMenu}
            downloadView={downloadView === true}
            includeSource={includeSource}
            type={type}
        />
    ) : (
        <DocumentDropContainer>
            {loading ? (
                <CircularProgress size={20} color="primary" />
            ) : Object.keys(savedDocuments).length === 0 && Object.keys(documentsUploading).length === 0 ? (
                <React.Fragment>
                    <StyledDropzoneContainer>
                        <StyledDropzoneComponent
                            accept={acceptedFileTypes}
                            disabled={disableUploads}
                            onDrop={(accepted, rejected) => {
                                handleAddDocuments(accepted, rejected, type);
                            }}
                        >
                            {({ getRootProps, getInputProps }) => (
                                <StyledDropzoneHelperContent
                                    className="dragdrop-instructions"
                                    data-testid="dragdrop-instructions"
                                    {...getRootProps()}
                                >
                                    <input {...getInputProps()} />
                                    <div>
                                        <IconStyled />
                                    </div>
                                    <StyledHelperTextContainer>
                                        <div>
                                            <h4 style={{ margin: 0 }}>Drop files on page to attach or browse here</h4>
                                        </div>
                                        <div style={{ color: "rgba(0, 0, 0, 0.54)" }}>
                                            (.csv, .doc, .docx, .jpg, .jpeg, .msg, .pdf, .png, .xls, and .xlsx files
                                            only)
                                        </div>
                                    </StyledHelperTextContainer>
                                </StyledDropzoneHelperContent>
                            )}
                        </StyledDropzoneComponent>
                    </StyledDropzoneContainer>

                    {renderHelperMessage(type)}
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <DocumentListRefactor
                        {...props}
                        handleAddDocuments={handleAddDocuments}
                        errors={errors}
                        header={type === "user" ? headers : role === "portlandBrokerAdmin" ? approvalHeaders : headers}
                        handleSort={handleDocumentSort}
                        columnToSort={columnToSort}
                        sortDirection={sortDirection}
                        selectDocumentType={updateDocument}
                        allDocumentTypes={allDocumentTypes}
                        documentTypes={documentTypes}
                        usedDocTypes={usedDocTypes}
                        handleOpenDocumentMenu={handleOpenDocumentMenu}
                        handleMenuClose={handleMenuClose}
                        toggleDeleteDialog={toggleDeleteDialog}
                        handlePreviewDocument={previewAndMenuClose}
                        anchorEl={anchorEl}
                        open={open}
                        docMenuKey={docKey}
                        savedDocuments={savedDocuments}
                        documentsUploading={documentsUploading}
                        formDisabled={formDisabled}
                        role={role}
                        approveDocument={handleApproveDocumentAndClose}
                        docApproved={Boolean(get(savedDocuments, docKey + ".approvedBy", null) != null)}
                        toggleTerminationDoc={toggleTerminationDoc}
                        terminationDocument={terminationDocument}
                        parentEntityType={parentType}
                        stateCode={stateCode}
                        adminListReady={adminListReady}
                        noHeader={noHeader}
                    />

                    <StyledDropzoneWAttachmentsContainer>
                        <StyledDropzoneComponentWAttachments
                            accept={acceptedFileTypes}
                            disabled={disableUploads}
                            onDrop={(accepted, rejected) => {
                                handleAddDocuments(accepted, rejected, type);
                            }}
                        >
                            {({ getRootProps, getInputProps }) => (
                                <StyledDropzoneHelperContent {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <div>
                                        <IconStyled />
                                    </div>
                                    <StyledHelperTextContainer>
                                        <div>
                                            <h4 style={{ margin: 0 }}>Drop files on page to attach or browse here</h4>
                                        </div>
                                        <div style={{ color: "rgba(0, 0, 0, 0.54)" }}>
                                            (.csv, .doc, .docx, .jpg, .jpeg, .msg, .pdf, .png, .xls, and .xlsx files
                                            only)
                                        </div>
                                    </StyledHelperTextContainer>
                                </StyledDropzoneHelperContent>
                            )}
                        </StyledDropzoneComponentWAttachments>
                    </StyledDropzoneWAttachmentsContainer>

                    {renderHelperMessage(type)}
                </React.Fragment>
            )}
            <Dialog open={deleteDialog} maxWidth="sm" aria-labelledby="confirmation-dialog-title">
                <DialogTitle style={{ textAlign: "center" }} id="confirmation-dialog-title">
                    Delete Document?
                </DialogTitle>
                <DialogContent>
                    Are you sure you want to permanently delete this document? You will not be able to recover it.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => toggleDeleteDialog(false, docToDelete)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={() => deleteAndMenuClose()} color="primary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </DocumentDropContainer>
    );
};

export default DocumentDrop;
